/**
 * usePressMutations — press station mutations (confirm press, report film
 * not-found / defective).
 *
 * STABLE HOOK: returns a memoized object whose member callbacks are stable
 * (useCallback over only `utils`). The tRPC mutation objects are captured in
 * refs (never placed in any consumer useEffect deps) — same class as the
 * documented useScanFeedback unstable-object gotcha (.3 review lesson).
 */
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { trpc } from '@/lib/trpc';

export function usePressMutations() {
  const utils = trpc.useUtils();

  const confirmPressMutation = trpc.pressing.confirmPress.useMutation();
  const reportNotFoundMutation = trpc.pressing.reportFilmNotFound.useMutation();
  const reportDefectiveMutation = trpc.pressing.reportFilmDefective.useMutation();

  // Capture mutation objects in refs so consumer effects never depend on them.
  const confirmRef = useRef(confirmPressMutation);
  const notFoundRef = useRef(reportNotFoundMutation);
  const defectiveRef = useRef(reportDefectiveMutation);
  useEffect(() => { confirmRef.current = confirmPressMutation; });
  useEffect(() => { notFoundRef.current = reportNotFoundMutation; });
  useEffect(() => { defectiveRef.current = reportDefectiveMutation; });

  const confirmPress = useCallback(
    (input: { garmentUnitId: string; filmUnitId: string; locationId: string; side: 'front' | 'back' }) =>
      confirmRef.current.mutateAsync(input),
    [],
  );

  const reportFilmNotFound = useCallback(
    (input: { garmentUnitId: string; locationId: string }) =>
      notFoundRef.current.mutateAsync(input),
    [],
  );

  const reportFilmDefective = useCallback(
    (input: { filmUnitId: string; locationId: string }) =>
      defectiveRef.current.mutateAsync(input),
    [],
  );

  return useMemo(
    () => ({
      confirmPress,
      reportFilmNotFound,
      reportFilmDefective,
      isConfirming: confirmPressMutation.isPending,
      isReporting: reportNotFoundMutation.isPending || reportDefectiveMutation.isPending,
      utils,
    }),
    [confirmPress, reportFilmNotFound, reportFilmDefective, confirmPressMutation.isPending, reportNotFoundMutation.isPending, reportDefectiveMutation.isPending, utils],
  );
}
